import { forwardRef, type HTMLAttributes } from "react";
import { cn } from "@/lib/cn";

export const Card = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "rounded-xl bg-surface text-text",
        "border border-border",
        // Subtle shadow in light mode for depth; drop it in dark mode and
        // rely on the surface-vs-bg lightness gap instead (shadows on dark
        // backgrounds read as muddy).
        "shadow-[0_1px_2px_rgb(15_23_42_/_0.04)] dark:shadow-none",
        "transition-[border-color,box-shadow,transform] duration-200 ease-out",
        className,
      )}
      {...props}
    />
  ),
);
Card.displayName = "Card";

/**
 * A Card that lifts subtly on hover/focus, intended for clickable cards
 * (chapter rows, dashboard tiles, study mode cards).
 */
export const InteractiveCard = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "rounded-xl bg-surface text-text",
        "border border-border",
        "shadow-[0_1px_2px_rgb(15_23_42_/_0.04)] dark:shadow-none",
        "transition-[border-color,box-shadow,transform] duration-200 ease-out",
        // Hover/focus lift -- 1px raise + slightly stronger shadow + brand-
        // tinted border so the card feels tactile.
        "hover:border-border-strong hover:-translate-y-px",
        "hover:shadow-[0_4px_12px_rgb(15_23_42_/_0.06)] dark:hover:bg-surface-2 dark:hover:shadow-none",
        "active:translate-y-0",
        className,
      )}
      {...props}
    />
  ),
);
InteractiveCard.displayName = "InteractiveCard";

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col gap-1 p-6", className)} {...props} />
  ),
);
CardHeader.displayName = "CardHeader";

export const CardTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3 ref={ref} className={cn("text-lg font-semibold leading-tight", className)} {...props} />
  ),
);
CardTitle.displayName = "CardTitle";

export const CardDescription = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn("text-sm text-text-muted", className)} {...props} />
));
CardDescription.displayName = "CardDescription";

export const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
  ),
);
CardContent.displayName = "CardContent";

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex items-center gap-3 p-6 pt-0", className)} {...props} />
  ),
);
CardFooter.displayName = "CardFooter";
